import clsx from 'clsx';
import React from 'react';
import { ComponentSize, TextColor } from '../../../types';
import { convertToPx } from '../../../utils';
import { ButtonColor, IconButton } from '../Button';
import { Icon, IconComponent, Icons } from '../Icons';
import { Portal } from '../Portal';
import { Typography, TypographyType } from '../Typography';
import { DialogProps, DialogSize } from './Dialog.types';

const DIALOG_SIZE_WIDTH_MAP: Record<DialogSize, number> = {
  [ComponentSize.X_SMALL]: 400,
  [ComponentSize.SMALL]: 500,
  [ComponentSize.MEDIUM]: 800,
};

const Dialog = ({
  children,
  isOpen,
  className,
  description,
  hideCloseButton,
  size = ComponentSize.SMALL,
  icon: iconProp,
  title,
  onClose,
}: DialogProps) => {
  const renderIcon = (icon: Icon | IconComponent) => {
    const iconSize = ComponentSize.LARGE;
    return typeof icon === 'string' ? (
      <Icons icon={icon} size={iconSize} />
    ) : (
      React.cloneElement(icon, { size: iconSize, ...icon.props })
    );
  };

  const renderDescription = () => {
    if (!description) return null;

    const textType = TypographyType.BASE;
    const color = TextColor.SECONDARY;

    return typeof description === 'string' ? (
      <Typography type={textType} color={color}>
        {description}
      </Typography>
    ) : (
      React.cloneElement(description, { type: textType, color, ...description.props })
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <dialog className="modal" open onClose={onClose}>
        {onClose && <div className="bg-neutral fixed inset-0 opacity-70" onClick={onClose} />}
        <div
          className={clsx('modal-box bg-base-100 flex max-w-none flex-col rounded-lg p-0 shadow-dialog', className)}
          style={{ width: convertToPx(typeof size === 'number' ? size : DIALOG_SIZE_WIDTH_MAP[size]) }}
        >
          {!hideCloseButton && onClose && (
            <div className="absolute right-2 top-2">
              <IconButton
                icon={Icon.CLOSE}
                onClick={onClose}
                size={ComponentSize.X_SMALL}
                color={ButtonColor.NAV_MENU}
              />
            </div>
          )}
          {(title || iconProp) && (
            <div className="flex items-center gap-2 bg-blue-900 p-6">
              {iconProp && renderIcon(iconProp)}
              <Typography type={TypographyType.H2} color="text-base-200">
                {title}
              </Typography>
            </div>
          )}
          <div className="flex flex-grow flex-col gap-6 overflow-auto p-6">
            {renderDescription()}
            {children}
          </div>
        </div>
      </dialog>
    </Portal>
  );
};

export default Dialog;
